import React from 'react';
import { LucideIcon, Loader2, AlertCircle, Search, Plus, FileX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

// Loading State Component
interface AdminLoadingStateProps {
  message?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const AdminLoadingState: React.FC<AdminLoadingStateProps> = ({
  message = 'Loading...',
  size = 'md',
  className,
}) => {
  const sizeClasses = {
    sm: 'py-8',
    md: 'py-12',
    lg: 'py-20',
  };

  const iconSizes = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
  };

  return (
    <div className={cn('flex flex-col items-center justify-center text-center', sizeClasses[size], className)}>
      <Loader2 className={cn('animate-spin text-primary mb-4', iconSizes[size])} />
      <p className="text-gray-600 font-medium">{message}</p>
    </div>
  );
};

// Empty State Component
interface AdminEmptyStateProps {
  icon?: LucideIcon;
  title: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
    icon?: LucideIcon;
  };
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const AdminEmptyState: React.FC<AdminEmptyStateProps> = ({
  icon: Icon = FileX,
  title,
  description,
  action,
  size = 'md',
  className,
}) => {
  const sizeClasses = {
    sm: 'py-8',
    md: 'py-12', 
    lg: 'py-20',
  };

  const iconSizes = {
    sm: 'w-12 h-12',
    md: 'w-16 h-16',
    lg: 'w-20 h-20',
  };

  const titleSizes = {
    sm: 'text-lg',
    md: 'text-xl',
    lg: 'text-2xl',
  };

  return (
    <div className={cn('flex flex-col items-center justify-center text-center', sizeClasses[size], className)}>
      <div className="flex items-center justify-center w-20 h-20 rounded-full bg-gray-100 mb-6">
        <Icon className={cn('text-gray-400', iconSizes[size])} />
      </div>
      <h3 className={cn('font-semibold text-gray-900 mb-2', titleSizes[size])}>
        {title}
      </h3>
      {description && (
        <p className="text-gray-600 mb-6 max-w-md leading-relaxed">
          {description}
        </p>
      )}
      {action && (
        <Button onClick={action.onClick} className="gap-2">
          {action.icon && <action.icon className="w-4 h-4" />}
          {action.label}
        </Button>
      )}
    </div>
  );
};

// Error State Component
interface AdminErrorStateProps {
  title?: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const AdminErrorState: React.FC<AdminErrorStateProps> = ({
  title = 'Something went wrong',
  description = 'We encountered an error while loading this data. Please try again.',
  action,
  size = 'md',
  className,
}) => {
  const sizeClasses = {
    sm: 'py-8',
    md: 'py-12',
    lg: 'py-20',
  };

  const iconSizes = {
    sm: 'w-12 h-12',
    md: 'w-16 h-16', 
    lg: 'w-20 h-20',
  };

  const titleSizes = {
    sm: 'text-lg',
    md: 'text-xl',
    lg: 'text-2xl',
  };

  return (
    <div className={cn('flex flex-col items-center justify-center text-center', sizeClasses[size], className)}>
      <div className="flex items-center justify-center w-20 h-20 rounded-full bg-red-50 mb-6">
        <AlertCircle className={cn('text-red-400', iconSizes[size])} />
      </div>
      <h3 className={cn('font-semibold text-gray-900 mb-2', titleSizes[size])}>
        {title}
      </h3>
      <p className="text-gray-600 mb-6 max-w-md leading-relaxed">
        {description}
      </p>
      {action && (
        <Button onClick={action.onClick} variant="outline">
          {action.label}
        </Button>
      )}
    </div>
  );
};

// Search Empty State Component
interface AdminSearchEmptyStateProps {
  searchTerm: string;
  onClear: () => void;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const AdminSearchEmptyState: React.FC<AdminSearchEmptyStateProps> = ({
  searchTerm,
  onClear,
  size = 'md',
  className,
}) => {
  const sizeClasses = {
    sm: 'py-8',
    md: 'py-12',
    lg: 'py-20',
  };

  return (
    <div className={cn('flex flex-col items-center justify-center text-center', sizeClasses[size], className)}>
      <div className="flex items-center justify-center w-20 h-20 rounded-full bg-gray-100 mb-6">
        <Search className="w-8 h-8 text-gray-400" />
      </div>
      <h3 className="text-xl font-semibold text-gray-900 mb-2">
        No results found
      </h3>
      <p className="text-gray-600 mb-6 max-w-md leading-relaxed">
        We couldn't find anything matching{' '}
        <span className="font-medium">"{searchTerm}"</span>. 
        Try adjusting your search terms.
      </p>
      <Button onClick={onClear} variant="outline">
        Clear search
      </Button>
    </div>
  );
}; 